<template>
  <div class="flex flex-col w-full">
    <div class="desktop-view flex justify-between w-full items-center">
      <div class="flex grow relative max-w-[288px]">
        <InputText
          v-if="showSearch"
          :key="resetFiltersCalled"
          class="search"
          size="small"
          form-type="search"
          id="search_text"
          :placeholder="searchPlaceholder"
          hide-label=""
          v-model="searchTerm"
          @input="updateSearchTerm"
          data-test="search-input"
        />
        <i class="fas fa-search absolute top-1/2 transform -translate-y-1/2 right-3 text-gray-400"></i>
      </div>
      <div class="flex items-center space-x-2">
        <Button
          variant="link"
          @click="drawerVisible = true"
          style="color: black"
          data-test="filters-button"
        >
          <img :src="Filters" />Filters</Button
        >
        <slot name="export-button"></slot>
      </div>
    </div>
    <div class="mobile-view flex flex-col w-full items-center">
      <div class="flex grow relative max-w-[288px]">
        <InputText
          v-if="showSearch"
          :key="resetFiltersCalled"
          class="search"
          size="small"
          form-type="search"
          id="search_text"
          :placeholder="searchPlaceholder"
          hide-label=""
          v-model="searchTerm"
          @input="updateSearchTerm"
          data-test="search-input"
        />
        <i class="fas fa-search absolute top-1/2 transform -translate-y-1/2 right-3 text-gray-400"></i>
      </div>
      <div class="flex items-center space-x-2 mt-2">
        <Button
          variant="link"
          @click="drawerVisible = true"
          style="color: black"
          data-test="filters-button"
        >
          <img :src="Filters" />Filters</Button
        >
        <slot name="export-button"></slot>
      </div>
    </div>
    <div :class="['flex flex-wrap gap-2 mt-2', { 'justify-start': !isMobileView, 'justify-center': isMobileView }]">
      <Chip
        v-if="selectedDateValue !== 'all'"
        :label="selectedDate.label"
        removable
        @remove="clearSelectedDate"
        class="chip-text"
        data-test="date-chip"
      />
      <Chip
        v-if="selectedStatusValue !== 'all'"
        :label="selectedStatus.label"
        removable
        @remove="clearSelectedStatus"
        class="chip-text"
        data-test="status-chip"
      />
      <Chip
        v-if="selectedFrequencyValue !== 'all'"
        :label="selectedFrequency.label"
        removable
        @remove="clearSelectedFrequency"
        class="chip-text"
        data-test="frequency-chip"
      />
      <Chip
        v-if="selectedSortValue !== 'default'"
        :label="selectedSort.label"
        removable
        @remove="clearSelectedSort"
        class="chip-text"
        data-test="sort-chip"
      />
    </div>
    <Drawer
      v-model:visible="drawerVisible"
      position="right"
      :showCloseIcon="false"
      :style="{ width: '430px' }"
      class="drawer-style"
      data-test="filters-drawer"
    >
      <template #header>
        <Button
          variant="link"
          @click="drawerVisible = false"
          data-test="close-drawer-button"
        >
          <img
            :src="NextArrow"
            alt="Close"
          />
        </Button>
        <div class="header-title">Refine Results</div>
      </template>
      <div v-if="showSortFilter">
        <Divider />
        <div
          class="flex cursor-pointer"
          @click="toggleSortFilter"
          data-test="sort-filter-toggle"
        >
          <img :src="sortFilterCollapsed ? ChevronRightDark : ChevronDownDark" />
          <div class="filter-label ml-2">Sort</div>
        </div>
        <div
          v-show="!sortFilterCollapsed"
          class="filter radio-button-group"
          data-test="sort-filter-options"
        >
          <div
            v-for="option in sortOptions"
            :key="option.value"
            class="flex items-center gap-2"
          >
            <RadioButton
              v-model="selectedSort"
              :inputId="option.value"
              name="sort"
              :value="option"
              data-test="sort-option"
            />
            <label :for="option.value">{{ option.label }}</label>
          </div>
        </div>
      </div>

      <div v-if="showDateFilter">
        <Divider />
        <div
          class="flex cursor-pointer"
          @click="toggleDateFilter"
          data-test="date-filter-toggle"
        >
          <img :src="dateFilterCollapsed ? ChevronRightDark : ChevronDownDark" />
          <div class="filter-label ml-2">Date</div>
        </div>
        <div
          v-show="!dateFilterCollapsed"
          class="filter radio-button-group"
          data-test="date-filter-options"
        >
          <div
            v-for="option in dateFilters"
            :key="option.value"
            class="flex items-center gap-2"
          >
            <RadioButton
              v-model="selectedDate"
              :inputId="option.value"
              name="date"
              :value="option"
              data-test="date-option"
            />
            <label :for="option.value">{{ option.label }}</label>
          </div>
        </div>
      </div>

      <div v-if="showStatusFilter">
        <Divider />
        <div
          class="flex cursor-pointer"
          @click="toggleStatusFilter"
          data-test="status-filter-toggle"
        >
          <img :src="statusFilterCollapsed ? ChevronRightDark : ChevronDownDark" />
          <div class="filter-label ml-2">Status</div>
        </div>
        <div
          v-show="!statusFilterCollapsed"
          class="filter radio-button-group"
          data-test="status-filter-options"
        >
          <div
            v-for="option in statusFilters"
            :key="option.value"
            class="flex items-center gap-2"
          >
            <RadioButton
              v-model="selectedStatus"
              :inputId="option.value"
              name="status"
              :value="option"
              data-test="status-option"
            />
            <label :for="option.value">{{ option.label }}</label>
          </div>
        </div>
      </div>

      <div v-if="showFrequencyFilter">
        <Divider />
        <div
          class="flex cursor-pointer"
          @click="toggleFrequencyFilter"
          data-test="frequency-filter-toggle"
        >
          <img :src="frequencyFilterCollapsed ? ChevronRightDark : ChevronDownDark" />
          <div class="filter-label ml-2">Frequency</div>
        </div>
        <div
          v-show="!frequencyFilterCollapsed"
          class="filter radio-button-group"
          data-test="frequency-filter-options"
        >
          <div
            v-for="option in frequencyFilters"
            :key="option.value"
            class="flex items-center gap-2"
          >
            <RadioButton
              v-model="selectedFrequency"
              :inputId="option.value"
              name="frequency"
              :value="option"
              data-test="frequency-option"
            />
            <label :for="option.value">{{ option.label }}</label>
          </div>
        </div>
      </div>

      <div
        v-if="filtersActive"
        class="center-button"
        data-test="clear-filters-button"
      >
        <Button
          variant="outlined"
          class="clear-filters-button"
          @click="resetFilters"
          >Clear Filters</Button
        >
      </div>
    </Drawer>
  </div>
</template>

<script setup>
import { ref, watch, computed, onUnmounted } from 'vue';
import ChevronRightDark from '@/assets/ChevronRightDark.svg';
import ChevronDownDark from '@/assets/ChevronDownDark.svg';
import NextArrow from '@/assets/NextArrow.svg';
import Filters from '@/assets/Filters.svg';

const props = defineProps({
  modelValue: String,
  selectedDate: Object,
  dateFilters: Array,
  selectedStatus: Object,
  statusFilters: Array,
  selectedFrequency: Object,
  frequencyFilters: Array,
  selectedSort: Object,
  sortOptions: Array,
  showSearch: {
    type: Boolean,
    default: true,
  },
  showDateFilter: {
    type: Boolean,
    default: true,
  },
  showStatusFilter: {
    type: Boolean,
    default: true,
  },
  showFrequencyFilter: {
    type: Boolean,
    default: true,
  },
  showSortFilter: {
    type: Boolean,
    default: true,
  },
  searchPlaceholder: {
    type: String,
    default: 'Search Organizations',
  },
});

const emit = defineEmits(['update:modelValue', 'applyFilters', 'resetFilters']);

const searchTerm = ref(props.modelValue);
const selectedDate = ref(props.selectedDate);
const selectedStatus = ref(props.selectedStatus);
const selectedFrequency = ref(props.selectedFrequency);
const selectedSort = ref(props.selectedSort);

watch(searchTerm, (newValue) => {
  emit('update:modelValue', newValue);
});

const updateSearchTerm = (event) => {
  searchTerm.value = event.target.value;
  emit('update:modelValue', searchTerm.value);
};

const selectedDateValue = computed(() => selectedDate.value || 'all');
const selectedStatusValue = computed(() => selectedStatus.value || 'all');
const selectedFrequencyValue = computed(() => selectedFrequency.value || 'all');
const selectedSortValue = computed(() => selectedSort.value || 'default');

const applyFilters = () => {
  emit('applyFilters', {
    selectedDate: selectedDate.value,
    selectedStatus: selectedStatus.value,
    selectedFrequency: selectedFrequency.value,
    selectedSort: selectedSort.value,
  });
};

watch(
  [
    selectedDate,
    selectedStatus,
    selectedFrequency,
    selectedSort,
    () => props.selectedDate,
    () => props.selectedStatus,
    () => props.selectedFrequency,
    () => props.selectedSort,
  ],
  ([newPropsSelectedDate, newPropsSelectedStatus, newPropsSelectedFrequency, newPropsSelectedSort]) => {
    if (newPropsSelectedDate !== selectedDate.value) {
      selectedDate.value = newPropsSelectedDate;
    }
    if (newPropsSelectedStatus !== selectedStatus.value) {
      selectedStatus.value = newPropsSelectedStatus;
    }
    if (newPropsSelectedFrequency !== selectedFrequency.value) {
      selectedFrequency.value = newPropsSelectedFrequency;
    }
    if (newPropsSelectedSort !== selectedSort.value) {
      selectedSort.value = newPropsSelectedSort;
    }
    applyFilters();
  },
  { deep: true },
);

const resetFiltersCalled = ref(0);

const resetFilters = () => {
  selectedDate.value = null;
  selectedStatus.value = null;
  selectedFrequency.value = null;
  selectedSort.value = null;
  searchTerm.value = '';
  resetFiltersCalled.value++;
  emit('resetFilters');
  emit('applyFilters', {
    selectedDate: null,
    selectedStatus: null,
    selectedFrequency: null,
    selectedSort: null,
  }); // Ensure filters are reset in parent component
};

const filtersActive = ref(false);
const combinedFilters = computed(() => ({
  searchTerm: searchTerm.value,
  selectedDate: selectedDateValue.value,
  selectedStatus: selectedStatusValue.value,
  selectedFrequency: selectedFrequencyValue.value,
  selectedSortValue: selectedSortValue.value,
}));

watch(
  combinedFilters,
  () => {
    filtersActive.value =
      searchTerm.value !== '' ||
      selectedDateValue.value !== 'all' ||
      selectedStatusValue.value !== 'all' ||
      selectedFrequencyValue.value !== 'all' ||
      selectedSortValue.value !== 'default';
  },
  { deep: true },
);

const drawerVisible = ref(false);

const sortFilterCollapsed = ref(true);
const dateFilterCollapsed = ref(true);
const statusFilterCollapsed = ref(true);
const frequencyFilterCollapsed = ref(true);

const toggleSortFilter = () => {
  sortFilterCollapsed.value = !sortFilterCollapsed.value;
};

const toggleDateFilter = () => {
  dateFilterCollapsed.value = !dateFilterCollapsed.value;
};

const toggleStatusFilter = () => {
  statusFilterCollapsed.value = !statusFilterCollapsed.value;
};

const toggleFrequencyFilter = () => {
  frequencyFilterCollapsed.value = !frequencyFilterCollapsed.value;
};

const clearSelectedDate = () => {
  selectedDate.value = 'all';
  applyFilters();
};

const clearSelectedStatus = () => {
  selectedStatus.value = 'all';
  applyFilters();
};

const clearSelectedFrequency = () => {
  selectedFrequency.value = null;
  applyFilters();
};

const clearSelectedSort = () => {
  selectedSort.value = 'default';
  applyFilters();
};

const isMobileView = ref(window.innerWidth <= 768);

const handleResize = () => {
  isMobileView.value = window.innerWidth <= 768;
};

window.addEventListener('resize', handleResize);

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
.drawer-style {
  width: 430px;
  height: 1578px;
  background: var(--White, #fff);
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.16);
}

.header-title {
  color: #000;
  text-align: center;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.filter-label {
  color: #000;
  font-family: Poppins;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}

.radio-button-group {
  display: flex;
  width: 288px;
  font-family: 'Poppins';
  padding: 12px 0px 12px 50px; /* Adjusted padding */
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  border-radius: 4px;
}

.clear-filters-button {
  display: flex;
  height: 44px;
  width: 274px;
  min-width: 100px;
  font-size: 18px;
  font-style: 'normal';
  font-family: 'Poppins';
  font-weight: 500;
  padding: 14px 18px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
}

.center-button {
  display: flex;
  justify-content: center;
  margin-top: 40px; /* Added margin-top */
}

.desktop-view {
  display: flex;
}

.mobile-view {
  display: none;
  justify-content: center; /* Center horizontally */
}

@media (max-width: 1024px) {
  .desktop-view {
    display: none;
  }
  .mobile-view {
    display: flex;
  }
}

.chip-text {
  font-family: Poppins;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
}
</style>
